/**
 * RBAC CONFIGURATION — Fix 1.16: Permission-Based Team Access
 *
 * 9 permissions mapped 1:1 to dashboard sections.
 * Sub-users have custom permissions stored in their DB record.
 * Main merchants have full access (no permission checking needed).
 * Old sub-users without custom permissions fall back to role-based lookup.
 */

// ── 9 Permission Definitions (1:1 with dashboard sections) ────────────────────

export const VALID_PERMISSIONS = [
  'view_transactions',
  'view_balances',
  'create_payouts',
  'manage_payment_links',
  'manage_wallets',
  'approve_withdrawals',
  'manage_team',
  'manage_api',
  'manage_settings',
];

export const PERMISSION_DEFINITIONS = [
  { key: 'view_transactions', label: 'Transactions', description: 'View transaction history', icon: 'ArrowLeftRight', group: 'view' },
  { key: 'view_balances', label: 'Balances & Payouts', description: 'View balance page and payout history', icon: 'Wallet', group: 'view' },
  { key: 'create_payouts', label: 'Create Payouts', description: 'Submit new payouts (moves funds)', icon: 'Send', group: 'action', isMoneySensitive: true },
  { key: 'manage_payment_links', label: 'Payment Links', description: 'Create and manage payment links', icon: 'Link', group: 'action' },
  { key: 'manage_wallets', label: 'Wallets', description: 'Create wallets and manage users', icon: 'Wallet2', group: 'action' },
  { key: 'approve_withdrawals', label: 'Withdrawals', description: 'Approve or reject withdrawal requests', icon: 'ShieldCheck', group: 'action', isMoneySensitive: true },
  { key: 'manage_team', label: 'Team Management', description: 'Add and manage team members', icon: 'Users', group: 'admin' },
  { key: 'manage_api', label: 'API & Developers', description: 'Manage API credentials', icon: 'Code', group: 'admin' },
  { key: 'manage_settings', label: 'Settings', description: 'Account settings', icon: 'Settings', group: 'admin' },
];

// ── Navigation → Permission Mapping ───────────────────────────────────────────

export const NAVIGATION_PERMISSIONS = {
  '/New-Dashboard/transactions': 'view_transactions',
  '/New-Dashboard/balances': 'view_balances',
  '/New-Dashboard/payments': 'manage_payment_links',
  '/New-Dashboard/wallet': 'manage_wallets',
  '/New-Dashboard/widthdrawal': 'approve_withdrawals',
  '/New-Dashboard/role-management': 'manage_team',
  '/New-Dashboard/api-docs': 'manage_api',
  '/New-Dashboard/settings': 'manage_settings',
};

// ── Role Hierarchy (backward compat fallback for old sub-users) ───────────────

export const ROLES = {
  merchant: { level: 5, label: "Main Merchant" },
  super_merchant: { level: 4, label: "Super Merchant" },
  sub_merchant: { level: 3, label: "Sub Merchant" },
  super_operation: { level: 2, label: "Super Operation" },
  operation: { level: 1, label: "Operation" },
  team_member: { level: 1, label: "Team Member" },
};

// Backward compat: role → default permissions (used when sub-user has no custom permissions)
export const ROLE_PERMISSIONS = {
  merchant: "ALL",
  super_merchant: [
    'view_transactions', 'view_balances', 'create_payouts',
    'manage_payment_links', 'manage_wallets', 'approve_withdrawals',
    'manage_team', 'manage_api', 'manage_settings',
  ],
  sub_merchant: [
    'view_transactions', 'view_balances', 'create_payouts',
    'manage_payment_links', 'manage_wallets', 'approve_withdrawals',
    'manage_team', 'manage_api',
  ],
  super_operation: [
    'view_transactions', 'approve_withdrawals',
    'manage_payment_links', 'create_payouts',
  ],
  operation: [
    'view_transactions', 'manage_payment_links',
  ],
  team_member: [], // No default — must have custom permissions
};

// ── Core Permission Functions ─────────────────────────────────────────────────

/**
 * Get stored permissions from localStorage (for sub-users).
 */
export const getStoredPermissions = () => {
  try {
    const stored = localStorage.getItem('userPermissions');
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Get user from localStorage.
 */
export const getUserFromStorage = () => {
  try {
    const userId = localStorage.getItem("userId");
    const userRole = localStorage.getItem("userRole");
    const isSubUser = localStorage.getItem("isSubUser") === "true";

    if (!userId || !userRole) return null;

    // Check session expiry
    const expiryTime = localStorage.getItem("token_expiry");
    if (expiryTime && Date.now() > parseInt(expiryTime)) {
      clearUserSession();
      return null;
    }

    return {
      id: userId,
      name: localStorage.getItem("userName") || "",
      email: localStorage.getItem("userEmail") || "",
      role: userRole,
      isSubUser,
      parentUser: localStorage.getItem("parentUser") || null,
      permissions: isSubUser ? getStoredPermissions() : [],
    };
  } catch (error) {
    console.error("getUserFromStorage: Error:", error);
    return null;
  }
};

/**
 * Clear user session.
 */
export const clearUserSession = () => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error("clearUserSession: Error:", error);
  }
};

/**
 * Get effective permissions for a user.
 * Sub-users with stored custom permissions → return those.
 * Otherwise → fall back to role-based permissions.
 */
export const getUserPermissions = (role, isMainUser = true, storedPermissions = null) => {
  try {
    if (!role) return [];

    // Main merchant → all permissions
    if (role === "merchant" && isMainUser) return VALID_PERMISSIONS;

    // Sub-user with custom stored permissions (new format)
    if (storedPermissions && Array.isArray(storedPermissions) && storedPermissions.length > 0) {
      const hasNewFormat = storedPermissions.some(p => VALID_PERMISSIONS.includes(p));
      if (hasNewFormat) return storedPermissions;
    }

    // Fallback to role config
    const permissions = ROLE_PERMISSIONS[role] || [];
    if (permissions === "ALL") return VALID_PERMISSIONS;
    return permissions;
  } catch (error) {
    console.error("getUserPermissions: Error:", error);
    return [];
  }
};

/**
 * Check if user has a specific permission.
 * Fix 1.16: Now accepts optional storedPermissions array for sub-users.
 */
export const hasPermission = (userRole, permission, isMainUser = true, storedPermissions = null) => {
  try {
    if (!userRole || !permission) return false;

    // Main merchant → full access
    if (userRole === "merchant" && isMainUser) return true;

    // Get effective permissions
    const permissions = getUserPermissions(userRole, isMainUser, storedPermissions);
    return permissions.includes(permission);
  } catch (error) {
    console.error("hasPermission: Error:", error);
    return false;
  }
};

/**
 * Check if user has any of the given permissions.
 */
export const hasAnyPermission = (userRole, permissionList, isMainUser = true, storedPermissions = null) => {
  try {
    if (!userRole || !Array.isArray(permissionList)) return false;
    return permissionList.some(p => hasPermission(userRole, p, isMainUser, storedPermissions));
  } catch (error) {
    console.error("hasAnyPermission: Error:", error);
    return false;
  }
};

/**
 * Check if user can perform an action (maps action to permission).
 */
export const canPerformAction = (userRole, action, isMainUser = true, storedPermissions = null) => {
  try {
    if (!userRole || !action) return false;
    if (userRole === "merchant" && isMainUser) return true;
    // Direct permission check — action names ARE the permission keys now
    return hasPermission(userRole, action, isMainUser, storedPermissions);
  } catch (error) {
    console.error("canPerformAction: Error:", error);
    return false;
  }
};

/**
 * Check if user can access a specific page path.
 */
export const canAccessPage = (userRole, pagePath, isMainUser = true, storedPermissions = null) => {
  try {
    if (userRole === "merchant" && isMainUser) return true;

    // Home/Dashboard is always accessible
    if (pagePath === '/New-Dashboard' || pagePath === '/New-Dashboard/') return true;

    // Find the matching permission for this path
    const requiredPermission = NAVIGATION_PERMISSIONS[pagePath];
    if (!requiredPermission) return true; // No permission required → allow

    return hasPermission(userRole, requiredPermission, isMainUser, storedPermissions);
  } catch (error) {
    console.error("canAccessPage: Error:", error);
    return false;
  }
};

/**
 * Check if user can manage another role (backward compat).
 */
export const canManageRole = (userRole, targetRole, isMainUser = true) => {
  try {
    if (!userRole || !targetRole) return false;
    if (userRole === "merchant" && isMainUser) return true;
    // Any sub-user with manage_team permission can manage team_member
    return false; // Role hierarchy no longer applies — only permissions matter
  } catch (error) {
    console.error("canManageRole: Error:", error);
    return false;
  }
};

/**
 * Get navigation items filtered by user permissions.
 * Used by NavigationMenu.jsx (old dashboard).
 */
export const getNavigation = (userRole, isMainUser = true, storedPermissions = null) => {
  try {
    if (!userRole) return [];

    // Build nav items from PERMISSION_DEFINITIONS mapped to routes
    return PERMISSION_DEFINITIONS
      .filter(def => {
        const permission = def.key;
        return hasPermission(userRole, permission, isMainUser, storedPermissions);
      })
      .map(def => ({
        id: def.key,
        label: def.label,
        permission: def.key,
      }));
  } catch (error) {
    console.error("getNavigation: Error:", error);
    return [];
  }
};

export default {
  VALID_PERMISSIONS,
  PERMISSION_DEFINITIONS,
  NAVIGATION_PERMISSIONS,
  ROLES,
  ROLE_PERMISSIONS,
  getStoredPermissions,
  getUserFromStorage,
  clearUserSession,
  getUserPermissions,
  hasPermission,
  hasAnyPermission,
  canPerformAction,
  canManageRole,
  canAccessPage,
  getNavigation,
};
